import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { translations } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("bscan_token"));
  const [user, setUser] = useState(null);
  const [language, setLanguage] = useState(() => localStorage.getItem("bscan_lang") || "en");
  const [loading, setLoading] = useState(true);
  const [appConfig, setAppConfig] = useState({ beta_mode: false, anon_max_messages: 2, magic_link_enabled: true });

  const t = useCallback(
    (path) => {
      const keys = path.split(".");
      let current = translations[language];
      for (const k of keys) {
        if (current && typeof current === "object") current = current[k];
        else return path;
      }
      return current || path;
    },
    [language]
  );

  const setLang = (l) => {
    setLanguage(l);
    localStorage.setItem("bscan_lang", l);
  };

  const api = axios.create({ baseURL: API });
  api.interceptors.request.use((cfg) => {
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  });

  const fetchMe = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
    } catch {
      localStorage.removeItem("bscan_token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Load public config once on boot (beta_mode flag, anon limits, etc.)
  useEffect(() => {
    axios.get(`${API}/config/public`)
      .then((r) => setAppConfig((prev) => ({ ...prev, ...(r.data || {}) })))
      .catch(() => { /* keep defaults */ });
  }, []);

  // First-touch UTM capture — stored once and reused on register/quick-start/claim-anonymous
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const utm_source = params.get("utm_source");
      const utm_medium = params.get("utm_medium");
      const utm_campaign = params.get("utm_campaign");
      const utm_content = params.get("utm_content");
      if (utm_source || utm_campaign) {
        const existing = JSON.parse(localStorage.getItem("bscan_utm") || "null");
        // Only set first-touch (don't overwrite)
        if (!existing) {
          localStorage.setItem("bscan_utm", JSON.stringify({
            utm_source: utm_source || null,
            utm_medium: utm_medium || null,
            utm_campaign: utm_campaign || null,
            utm_content: utm_content || null,
            captured_at: new Date().toISOString(),
          }));
        }
      }
    } catch (e) { /* ignore */ void e; }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("bscan_token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const tryRedeemInvite = async (token) => {
    try {
      const code = localStorage.getItem("bscan_invite_code");
      if (code) {
        await axios.post(`${API}/promo/redeem`, { code }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        localStorage.removeItem("bscan_invite_code");
      }
    } catch (e) { /* silent */ void e; }
    try {
      const refCode = localStorage.getItem("bscan_referral_code");
      if (refCode) {
        await axios.post(`${API}/referral/claim-bonus`, { referral_code: refCode }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        localStorage.removeItem("bscan_referral_code");
      }
    } catch (e) { /* silent */ void e; }
  };

  const register = async (email, password, full_name, opts = {}) => {
    let referral_source = null;
    try {
      const stored = localStorage.getItem("bscan_referral");
      if (stored) referral_source = JSON.parse(stored)?.source || null;
    } catch (e) { /* ignore */ void e; }
    const body = {
      email,
      password,
      full_name,
      accept_terms: !!opts.accept_terms,
      accept_terms_version: opts.accept_terms_version || "2026-02-v1",
    };
    if (referral_source) body.referral_source = referral_source;
    // First-touch UTM attribution
    try {
      const utm = JSON.parse(localStorage.getItem("bscan_utm") || "null");
      if (utm) {
        if (utm.utm_source) body.utm_source = utm.utm_source;
        if (utm.utm_medium) body.utm_medium = utm.utm_medium;
        if (utm.utm_campaign) body.utm_campaign = utm.utm_campaign;
        if (utm.utm_content) body.utm_content = utm.utm_content;
      }
    } catch (e) { /* ignore */ void e; }
    const res = await axios.post(`${API}/auth/register`, body);
    localStorage.setItem("bscan_token", res.data.access_token);
    setToken(res.data.access_token);
    // Auto-redeem invite if present (must run before setUser so subscription_status reflects redemption)
    await tryRedeemInvite(res.data.access_token);
    // Refresh user to pick up any subscription updates from the redemption
    try {
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${res.data.access_token}` } });
      setUser(me.data);
    } catch {
      setUser(res.data.user);
    }
    try { localStorage.removeItem("bscan_referral"); } catch (e) { /* ignore */ void e; }
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("bscan_token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = fetchMe;

  return (
    <AppContext.Provider value={{ token, user, language, setLang, t, login, register, logout, api, loading, refreshUser, appConfig, setToken, setUser }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
};
